import { useCallback, useLayoutEffect, useMemo, useRef } from 'react';

const DURATION_MS = 350;
const EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

export const useFlip = () => {
  const nodesRef = useRef(new Map());
  const callbacksRef = useRef(new Map());
  const firstRectsRef = useRef(null);

  // One stable callback per id, cached — otherwise React detaches and
  // reattaches every chip's ref on every render.
  const registerNode = useCallback((id) => {
    let cb = callbacksRef.current.get(id);
    if (!cb) {
      cb = (el) => {
        if (el) nodesRef.current.set(id, el);
        else nodesRef.current.delete(id);
      };
      callbacksRef.current.set(id, cb);
    }
    return cb;
  }, []);

  const prepare = useCallback(() => {
    const rects = new Map();
    nodesRef.current.forEach((el, id) => rects.set(id, el.getBoundingClientRect()));
    firstRectsRef.current = rects;
  }, []);

  useLayoutEffect(() => {
    const firstRects = firstRectsRef.current;
    if (!firstRects) return;
    firstRectsRef.current = null;
    if (prefersReducedMotion()) return;

    nodesRef.current.forEach((el, id) => {
      const first = firstRects.get(id);
      if (!first) return;
      const last = el.getBoundingClientRect();
      const dx = first.left - last.left;
      const dy = first.top - last.top;
      if (!dx && !dy) return;

      el.style.transition = 'none';
      el.style.transform = `translate(${dx}px, ${dy}px)`;
      el.getBoundingClientRect(); // force reflow
      el.style.transition = `transform ${DURATION_MS}ms ${EASING}`;
      el.style.transform = '';
    });
  });

  return useMemo(() => ({ registerNode, prepare }), [registerNode, prepare]);
};

export default useFlip;
